import React, {
    createContext,
    FunctionComponent,
    PropsWithChildren,
    useContext
} from 'react'
import * as ThemeUI from 'theme-ui'
import {
    useFormContext,
    FormProvider,
    SubmitHandler,
    UseFormMethods,
    RegisterOptions
} from 'react-hook-form'

import { Text, Box, Whisper, BoxProps } from '..'
import omit from 'lodash.omit'
import get from 'lodash.get'

export * from './Radio'
export * from './Checkbox'

export interface TiaCoreInputProps {
    /**
     * name of field
     */
    name: string

    /**
     * is this field required
     */
    required?: boolean

    /**
     * validations passed through to react-hook-form
     */
    validations?: RegisterOptions

    /**
     * error to show when validations fail
     */
    errorMessage?: string | { [key: string]: string }
}

// Custom Form context
const FormCustomContext = createContext<{
    /**
     * loading flag
     */
    loading?: boolean
}>({})

// logic for deriving an error message out of props and form state
function useErrorMessage(
    name: string,
    messages?: string | { [key: string]: string }
) {
    const formContext = useFormContext()
    let error = formContext?.errors || null

    if (error) {
        error = get(formContext.errors, name)
    }

    let errorText = ''

    if (error) {
        if (typeof messages === 'string') {
            // errorMessage was passed-in as a string
            errorText = messages
        } else if (messages) {
            // we have an object of type-to-error mappings
            errorText = messages[error.type]
        } else if (error?.message) {
            // use message provided by error
            errorText = error.message
        } else if (error.type === 'required') {
            // required fields show a default message
            errorText = 'Required field'
        }
    }

    return [!!error, errorText]
}

// helper method to determine if field values are empty
function isEmptyValue(value: any) {
    return (
        value === undefined ||
        value?.toString() === '' ||
        value?.length === 0 ||
        value === null
    )
}

export function ErrorLabel({
    show,
    children
}: PropsWithChildren<{ show: boolean }>) {
    if (!show) {
        return null
    }

    return (
        <Text
            sx={{
                color: 'poppy',
                fontSize: 0,
                mb: 5,
                mt: (t: ThemeUI.Theme) => `-${t.sizes?.[4]}px`
            }}
            as="div"
        >
            {children}
        </Text>
    )
}

// shared form logic for Tia inputs
export function useTiaInput(props: TiaCoreInputProps) {
    const { required, name, errorMessage, validations } = props

    const formContext = useFormContext()
    const [error, errorText] = useErrorMessage(name, errorMessage)
    const { loading } = useContext(FormCustomContext)

    // using watch to update the component accordingly
    let borderColor =
        required && isEmptyValue(formContext?.watch(name))
            ? 'inputOutlineRequired'
            : 'inputOutline'

    if (error) {
        borderColor = 'inputOutlineError'
    }

    return {
        // error to print (an error without errorMessage is possible)
        error: (
            <ErrorLabel show={Boolean(error && errorText)}>
                {errorText}
            </ErrorLabel>
        ),
        borderColor,
        register: formContext?.register({ required, ...validations }),
        control: formContext?.control,
        loading
    }
}

//
// This section includes copypasta
// Solving with render props thwarted by TypeScript generics and ref forwarding
// The only difference between components are:
// - which ThemeUI component renders and the
// - prop type
//
export interface InputProps
    extends TiaCoreInputProps,
        Omit<ThemeUI.InputProps, 'name' | 'onChange'> {}
export const Input = (props: InputProps) => {
    const { error, borderColor, register, loading } = useTiaInput(props)

    if (loading) {
        return <Whisper sx={{ mb: 5, height: 7, ...props.sx }} />
    }

    return (
        <>
            <ThemeUI.Input
                sx={{ borderColor, ...props.sx }}
                {...omit(props, 'sx')}
                ref={register}
            />
            {error}
        </>
    )
}

interface TextareaProps
    extends TiaCoreInputProps,
        Omit<ThemeUI.TextareaProps, 'name'> {}
export const Textarea = (props: TextareaProps) => {
    const { error, borderColor, register, loading } = useTiaInput(props)

    if (loading) {
        return <Whisper sx={{ mb: 5, height: 8, ...props.sx }} />
    }

    return (
        <>
            <ThemeUI.Textarea
                sx={{ borderColor, ...props.sx }}
                {...omit(props, 'sx')}
                ref={register}
            />
            {error}
        </>
    )
}

interface SliderProps
    extends TiaCoreInputProps,
        Omit<ThemeUI.SliderProps, 'name'> {}
export const Slider = (props: SliderProps) => {
    const { error, borderColor, register, loading } = useTiaInput(props)

    const isDisabled = props.disabled || loading

    return (
        <>
            <ThemeUI.Slider
                disabled={isDisabled}
                sx={{
                    borderColor,
                    backgroundColor: isDisabled
                        ? 'backgroundDisabled'
                        : undefined,
                    ...props.sx
                }}
                {...omit(props, 'sx')}
                ref={register}
            />
            {error}
        </>
    )
}

export type FormProps = {
    /**
     * function to call when submitting the form
     */
    onSubmit: SubmitHandler<Record<string, any>>

    /**
     * methods returned from useForm()
     */
    useFormMethods: UseFormMethods

    /**
     * loading flag
     */
    loading?: boolean
} & BoxProps

/**
 * Renders a Tia form wrapped in react-hook-form context provider
 */
export const Form: FunctionComponent<
    React.PropsWithChildren<PropsWithChildren<FormProps>>
> = ({ onSubmit, useFormMethods, children, loading, ...props }) => {
    return (
        <FormCustomContext.Provider value={{ loading }}>
            <FormProvider {...useFormMethods}>
                <Box
                    as="form"
                    onSubmit={useFormMethods?.handleSubmit(onSubmit)}
                    {...props}
                >
                    {children}
                </Box>
            </FormProvider>
        </FormCustomContext.Provider>
    )
}
